/* =====================================================================
 * AdminDashboardPage — 본부 / 부서별 토큰 사용량 (테넌트 전체).
 *
 * 사이드바 "관리" 섹션 맨 아래, 개인 대시보드 바로 다음. 개인
 * 대시보드(`DashboardPage`)와 달리 테넌트 전체를 읽으므로, 서버가
 * 부서코드 허용목록으로 접근을 막는다. 이 화면의 게이트는 표시용일
 * 뿐이고 실제 판정은 전부 서버에 있다 — 클라이언트 게이트만으로는
 * 우회 가능하므로 둘 다 필요하다.
 *
 * 구성(위 → 아래):
 *   1. 요약 타일        총 토큰 / 전기간 대비 / 부서 수 / 평균 / 수집률
 *   2. 사용량 추이      최근 7·30·90·365일·전체 프리셋 및 날짜 범위 전환
 *   3. 본부 > 부서      사용량 순위. 행을 누르면 위 차트가 그 부서로 좁혀진다
 *
 * 집계값만 다룬다. 프롬프트 내용이나 개별 실행 기록은 이 화면에 없다.
 * ===================================================================== */

import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTheme } from 'styled-components';
import {
  Building2,
  ChartPie,
  Coins,
  Download,
  Loader2,
  RefreshCw,
  TrendingDown,
  TrendingUp,
  Users,
} from 'lucide-react';
import {
  ADMIN_DASHBOARD_MAX_RANGE_DAYS,
  AdminDashboardForbiddenError,
  DashboardUnavailableError,
  fetchAdminDashboardBreakdown,
  fetchAdminDashboardExport,
  fetchAdminDashboardSeries,
  fetchAdminDashboardSummary,
} from '@auw/api-client';

import { SelectDropdown, type SelectOption } from '../common';
import { RingSpinner } from '../common/Spinner';
import { StatTile } from '../dashboard/StatTile';
import {
  formatDashboardTokenCount,
  validateDashboardDateRange,
  type UsageMetric,
} from '../dashboard/dashboardMetrics';
import type { ChartMode } from '../dashboard/chartPalette';
import {
  ActionButton,
  Card,
  CardHead,
  CardHint,
  CardHintLine,
  CardTitle,
  ChartFrame,
  ChartLoading,
  DateField,
  DateInput,
  DateRangeButton,
  DateRangeError,
  DateRangeForm,
  DepartmentField,
  EmptyState,
  HeaderActions,
  PageHeader,
  PageHeaderText,
  PageInner,
  PageScroll,
  PageSubtitle,
  PageTitle,
  RefreshButton,
  Segmented,
  SegmentedButton,
  SegmentedControlCluster,
  SegmentedDivider,
  SegmentedGroups,
  StatGrid,
} from '../dashboard/dashboard.styles';
import { AdminUsageChart } from './AdminUsageChart';
import { OrgBreakdown } from './OrgBreakdown';
import {
  adminExportFileName,
  buildAdminDashboardSheets,
  writeAdminDashboardWorkbook,
} from './adminDashboardExport';
import {
  ADMIN_DASHBOARD_DEFAULT_RANGE_DAYS,
  ADMIN_DASHBOARD_RANGE_DAYS,
  granularityForWindow,
} from './adminDashboardRange';

const USAGE_METRICS: UsageMetric[] = ['tokens', 'runs'];

/** Summary window. Fixed at 30 days so the 전기간 대비 comparison has a
 *  stable meaning regardless of what the chart below is showing. */
const SUMMARY_DAYS = 30;

/*
 * 차트와 하단 표는 같은 기간 프리셋(`ADMIN_DASHBOARD_RANGE_DAYS`)을 쓰되
 * 각자의 상태를 가진다. 차트의 버킷 단위(일·주·월)는 고르는 것이 아니라
 * 보고 있는 기간에서 따라온다 — 예전에는 "일별 / 주별 / 월별 / 연별" 을
 * 골랐고 그 선택이 기간까지 같이 바꿨는데, 버튼 이름만 보면 눈금만 바뀔 것
 * 같아서 왜 합계가 달라지는지 알 수 없었다. 표는 버킷이 없는 단순 합계라
 * 기간만 고르면 된다.
 */

/** Nothing on this page moves second to second; refresh on focus and on
 *  demand rather than on a timer. A tenant-wide scan is not free. */
const STALE_MS = 60_000;

export const AdminDashboardPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const chartMode: ChartMode = theme.mode === 'dark' ? 'dark' : 'light';
  const queryClient = useQueryClient();

  /* 차트의 프리셋 기간. 사용자 지정 날짜 범위가 적용되면 그쪽이 우선한다. */
  const [chartDays, setChartDays] = useState<number>(
    ADMIN_DASHBOARD_DEFAULT_RANGE_DAYS,
  );
  const [usageMetric, setUsageMetric] = useState<UsageMetric>('tokens');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [dateRangeError, setDateRangeError] = useState<
    'incomplete' | 'invalid' | 'tooLong' | null
  >(null);
  const [appliedDateRange, setAppliedDateRange] = useState<{
    start: string;
    end: string;
  } | null>(null);
  const [scope, setScope] = useState<{
    orgCode?: string;
    departmentCode?: string;
  }>({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  /* 하단 표의 기간. 차트와 독립이며 기본값은 요약 타일과 같은 30일이다. */
  const [breakdownDays, setBreakdownDays] = useState<number>(SUMMARY_DAYS);

  /* 차트가 실제로 보는 구간(프리셋 또는 사용자 지정 범위)에 맞는 버킷 단위. */
  const granularity = useMemo(
    () => granularityForWindow(appliedDateRange, chartDays),
    [appliedDateRange, chartDays],
  );

  const summary = useQuery({
    queryKey: ['adminDashboard', 'summary'],
    queryFn: () => fetchAdminDashboardSummary(SUMMARY_DAYS),
    staleTime: STALE_MS,
    refetchOnWindowFocus: true,
  });

  /**
   * 차트 스코프용 breakdown — 부서 필터 목록과 선택된 부서 이름의 출처.
   *
   * 차트의 기간을 따른다. 부서 필터는 차트를 좁히는 컨트롤이므로, 차트가
   * 보고 있는 구간에 사용 기록이 있는 부서를 골라야 한다. 아래 표의
   * 기간을 따르게 하면 표를 7일로 줄인 순간 차트에 데이터가 있는 부서를
   * 고를 수 없게 된다.
   */
  const breakdown = useQuery({
    queryKey: [
      'adminDashboard',
      'breakdown',
      appliedDateRange?.start ?? 'preset',
      appliedDateRange?.end ?? chartDays,
    ],
    queryFn: () =>
      fetchAdminDashboardBreakdown(chartDays, appliedDateRange ?? undefined),
    staleTime: STALE_MS,
  });

  /**
   * 하단 표용 breakdown — 자체 기간 선택기를 따른다.
   *
   * 위 쿼리와 키 모양이 같아서 두 기간이 일치하면 React Query 가 같은
   * 캐시를 공유한다 (요청 1건). 다를 때만 2건이 나간다.
   */
  const breakdownTable = useQuery({
    queryKey: ['adminDashboard', 'breakdown', 'preset', breakdownDays],
    queryFn: () => fetchAdminDashboardBreakdown(breakdownDays),
    staleTime: STALE_MS,
  });

  const series = useQuery({
    queryKey: [
      'adminDashboard',
      'series',
      granularity,
      appliedDateRange?.start ?? 'preset',
      appliedDateRange?.end ?? chartDays,
      scope.orgCode ?? '',
      scope.departmentCode ?? '',
    ],
    queryFn: () =>
      fetchAdminDashboardSeries(granularity, chartDays, {
        range: appliedDateRange ?? undefined,
        orgCode: scope.orgCode,
        departmentCode: scope.departmentCode,
      }),
    staleTime: STALE_MS,
    /* 기간·부서를 바꾸는 동안 이전 그래프를 흐리게 남겨 둔다(ChartFrame). */
    placeholderData: (previousData) => previousData,
  });

  const refreshAll = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await queryClient.invalidateQueries({
        queryKey: ['adminDashboard'],
        refetchType: 'active',
      });
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, queryClient]);

  const applyDateRange = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      const error = validateDashboardDateRange(
        dateStart,
        dateEnd,
        ADMIN_DASHBOARD_MAX_RANGE_DAYS,
      );
      setDateRangeError(error);
      if (error) return;
      setAppliedDateRange({ start: dateStart, end: dateEnd });
    },
    [dateEnd, dateStart],
  );

  const clearDateRange = useCallback(() => {
    setDateStart('');
    setDateEnd('');
    setDateRangeError(null);
    setAppliedDateRange(null);
  }, []);

  /** 프리셋과 사용자 지정 범위는 배타적이다 — 범위가 적용된 채로 프리셋을
   *  누르면 아무 변화가 없어 버튼이 고장난 것처럼 보이므로 범위를 거둔다. */
  const selectChartDays = useCallback(
    (days: number) => {
      setChartDays(days);
      clearDateRange();
    },
    [clearDateRange],
  );

  /** 차트·표 공용 기간 버튼 이름. 보존기간 전체는 "전체"로 읽는다. */
  const rangeLabel = useCallback(
    (days: number) =>
      days === ADMIN_DASHBOARD_MAX_RANGE_DAYS
        ? t('common:adminDashboard.rangeAll')
        : t('common:adminDashboard.rangeDays', { days }),
    [t],
  );

  /** ``unknown`` is a real bucket (a login before 부서코드 capture, or a
   *  deleted user), so it gets a label rather than being hidden — the
   *  rollup has to reconcile with the tenant-wide total. */
  const labelFor = useCallback(
    (code: string, name: string | null) => {
      if (name) return name;
      if (code === 'unknown') return t('common:adminDashboard.unknownOrg');
      return code;
    },
    [t],
  );

  /**
   * 부서 필터 옵션.
   *
   * breakdown 응답을 재사용한다: 이미 이 기간의 모든 본부·부서를 이름까지
   * 담고 있으므로 목록 전용 API 를 새로 부를 이유가 없다. 대신 현재 기간에
   * 사용 기록이 없는 부서는 목록에 안 나온다 — 고를 수 있는 부서가 곧
   * 아래 표에 있는 부서라서, 골랐는데 빈 차트가 나오는 일이 없다.
   *
   * 소속 본부는 ``description`` 으로 붙인다. 부서명만으로는 어느 본부인지
   * 모르고, 부서명이 겹치는 경우 둘을 구분할 방법이 없다.
   */
  const departmentOptions = useMemo<SelectOption[]>(() => {
    const all: SelectOption = {
      value: '',
      label: t('common:adminDashboard.filterAllDepartments'),
    };
    if (!breakdown.data) return [all];
    return [
      all,
      ...breakdown.data.orgs.flatMap((org) =>
        org.departments.map((dept) => ({
          value: `${org.org_code}\u0000${dept.department_code}`,
          label: labelFor(dept.department_code, dept.department_name),
          description: labelFor(org.org_code, org.org_name),
        })),
      ),
    ];
  }, [breakdown.data, labelFor, t]);

  /** 현재 scope 에 대응하는 select value. 표에서 행을 눌러 scope 가 바뀌어도
   *  select 가 같이 따라 움직여야 두 컨트롤이 어긋나 보이지 않는다. */
  const departmentValue =
    scope.orgCode && scope.departmentCode
      ? `${scope.orgCode}\u0000${scope.departmentCode}`
      : '';

  const selectDepartment = useCallback((value: string) => {
    if (!value) {
      setScope({});
      return;
    }
    const [orgCode, departmentCode] = value.split('\u0000');
    setScope({ orgCode, departmentCode });
  }, []);

  const forbidden = useMemo(
    () =>
      [summary.error, breakdown.error, breakdownTable.error, series.error].some(
        (error) => error instanceof AdminDashboardForbiddenError,
      ),
    [summary.error, breakdown.error, breakdownTable.error, series.error],
  );
  const unavailable = useMemo(
    () =>
      [summary.error, breakdown.error, breakdownTable.error, series.error].some(
        (error) => error instanceof DashboardUnavailableError,
      ),
    [summary.error, breakdown.error, breakdownTable.error, series.error],
  );

  const numberFormat = useMemo(
    () => new Intl.NumberFormat(i18n.language),
    [i18n.language],
  );

  /* 전기간 대비 증감. 이전 기간이 0 이면 비율은 의미가 없으므로 표시하지
   * 않는다 — 0 에서 늘어난 것은 무한 증가라 백분율로 읽을 수 없다. */
  const delta = useMemo(() => {
    if (!summary.data) return null;
    const now = summary.data.total_token_count;
    const before = summary.data.previous.total_token_count;
    if (before <= 0) return null;
    return Math.round(((now - before) / before) * 1000) / 10;
  }, [summary.data]);

  /* 합계에 붙는 각주로만 남긴다 — 토큰이 안 잡힌 실행이 있으면 부분합을
   * 실제 감소로 읽게 되므로, 전용 타일을 제거해도 이 경고는 필요하다. */
  const coverageHint =
    summary.data && summary.data.unknown_token_run_count > 0
      ? t('common:dashboard.tokenCoverage', {
          known: summary.data.token_run_count,
          total: summary.data.total_runs,
        })
      : undefined;

  /**
   * 엑셀 내보내기.
   *
   * 화면의 기간·집계 단위를 그대로 쓰되 **부서 선택은 무시한다** — 부서마다
   * 시트를 만드는 것이 목적이라, 한 부서로 좁힌 상태에서 눌렀다고 그 부서만
   * 담으면 "부서별 시트"가 성립하지 않는다.
   *
   * 워크북 데이터는 그때 새로 받는다. 화면의 series 는 현재 스코프 한 개뿐이고
   * 부서별 시트에는 부서마다의 시계열이 필요하다.
   */
  const runExport = useCallback(async () => {
    if (isExporting) return;
    setIsExporting(true);
    setExportError(null);
    try {
      const data = await fetchAdminDashboardExport(
        granularity,
        chartDays,
        appliedDateRange ?? undefined,
      );
      const sheets = buildAdminDashboardSheets(
        data,
        {
          summarySheet: t('common:adminDashboard.exportSummarySheet'),
          period: t('common:adminDashboard.exportPeriod'),
          granularity: t('common:dashboard.granularityTabLabel'),
          totalTokens: t('common:adminDashboard.exportTotalTokens'),
          totalRuns: t('common:adminDashboard.exportTotalRuns'),
          org: t('common:adminDashboard.columnOrg'),
          department: t('common:adminDashboard.columnDepartment'),
          users: t('common:adminDashboard.columnUsers'),
          avgPerRun: t('common:adminDashboard.statAvgPerRun'),
          untrackedRuns: t('common:adminDashboard.exportUntrackedRuns'),
          bucket: t('common:adminDashboard.exportBucket'),
          unknownOrg: t('common:adminDashboard.unknownOrg'),
          generatedAt: t('common:adminDashboard.exportGeneratedAt'),
        },
        {
          summary: summary.data,
          breakdown: breakdown.data,
          granularityLabel: t(`common:dashboard.granularity.${granularity}`),
          generatedAt: new Date(),
        },
      );
      await writeAdminDashboardWorkbook(
        sheets,
        adminExportFileName(data.start, data.end),
      );
    } catch (error) {
      // 실패를 조용히 넘기면 버튼을 눌렀는데 아무 일도 안 난 것처럼 보인다.
      setExportError(
        error instanceof AdminDashboardForbiddenError
          ? t('common:adminDashboard.forbidden')
          : t('common:adminDashboard.exportFailed'),
      );
    } finally {
      setIsExporting(false);
    }
  }, [
    appliedDateRange,
    breakdown.data,
    chartDays,
    granularity,
    isExporting,
    summary.data,
    t,
  ]);

  const scopeLabel = useMemo(() => {
    if (!scope.departmentCode) return null;
    const org = breakdown.data?.orgs.find(
      (candidate) => candidate.org_code === scope.orgCode,
    );
    const dept = org?.departments.find(
      (candidate) => candidate.department_code === scope.departmentCode,
    );
    if (!dept) return scope.departmentCode;
    return labelFor(dept.department_code, dept.department_name);
  }, [breakdown.data, labelFor, scope.departmentCode, scope.orgCode]);

  if (forbidden) {
    return (
      <PageScroll>
        <PageInner>
          <Card>
            <EmptyState>{t('common:adminDashboard.forbidden')}</EmptyState>
          </Card>
        </PageInner>
      </PageScroll>
    );
  }

  return (
    <PageScroll>
      <PageInner>
        <PageHeader>
          <PageHeaderText>
            <PageTitle>{t('common:adminDashboard.title')}</PageTitle>
            <PageSubtitle>{t('common:adminDashboard.subtitle')}</PageSubtitle>
          </PageHeaderText>
          <HeaderActions>
            <ActionButton
              type="button"
              onClick={() => void runExport()}
              disabled={isExporting || unavailable}
              title={t('common:adminDashboard.exportHint')}
            >
              {isExporting ? (
                <Loader2 aria-hidden className="spin" />
              ) : (
                <Download aria-hidden />
              )}
              {t('common:adminDashboard.export')}
            </ActionButton>
            <RefreshButton
              type="button"
              onClick={() => void refreshAll()}
              disabled={isRefreshing}
              $busy={isRefreshing}
              aria-label={t('common:dashboard.refresh')}
              title={t('common:dashboard.refresh')}
            >
              <RefreshCw aria-hidden />
            </RefreshButton>
          </HeaderActions>
        </PageHeader>
        {exportError && (
          <Card>
            <EmptyState>{exportError}</EmptyState>
          </Card>
        )}

        {unavailable ? (
          <Card>
            <EmptyState>{t('common:dashboard.unavailable')}</EmptyState>
          </Card>
        ) : (
          <>
            {/* 1 — 요약 */}
            <StatGrid $columns={4}>
              <StatTile
                icon={Coins}
                label={t('common:adminDashboard.statTotalTokens', {
                  days: SUMMARY_DAYS,
                })}
                value={
                  summary.data
                    ? formatDashboardTokenCount(summary.data.total_token_count)
                    : '—'
                }
                unit={t('common:dashboard.unitTokens')}
                hint={coverageHint}
                title={
                  summary.data
                    ? `${numberFormat.format(
                        summary.data.total_token_count,
                      )} ${t('common:dashboard.unitTokens')}`
                    : undefined
                }
              />
              <StatTile
                icon={delta != null && delta < 0 ? TrendingDown : TrendingUp}
                label={t('common:adminDashboard.statDelta')}
                value={
                  delta == null
                    ? '—'
                    : `${delta > 0 ? '+' : ''}${delta.toLocaleString(
                        i18n.language,
                      )}`
                }
                unit={delta == null ? undefined : '%'}
                hint={
                  summary.data
                    ? t('common:adminDashboard.statDeltaHint', {
                        value: formatDashboardTokenCount(
                          summary.data.previous.total_token_count,
                        ),
                      })
                    : undefined
                }
              />
              <StatTile
                icon={Building2}
                label={t('common:adminDashboard.statDepartments')}
                value={summary.data?.department_count ?? '—'}
                unit={t('common:adminDashboard.unitDepartments')}
                hint={
                  summary.data
                    ? t('common:adminDashboard.statOrgHint', {
                        count: summary.data.org_count,
                      })
                    : undefined
                }
              />
              <StatTile
                icon={Users}
                label={t('common:adminDashboard.statAvgPerRun')}
                value={
                  summary.data?.avg_token_per_run != null
                    ? formatDashboardTokenCount(
                        summary.data.avg_token_per_run,
                      )
                    : '—'
                }
                unit={t('common:dashboard.unitTokens')}
              />
            </StatGrid>

            {/* 2 — 사용량 추이 */}
            <Card>
              <CardHead>
                <CardTitle>
                  <ChartPie aria-hidden size={16} />
                  {scopeLabel
                    ? t('common:adminDashboard.usageTitleScoped', {
                        scope: scopeLabel,
                      })
                    : t('common:adminDashboard.usageTitle')}
                </CardTitle>
                <SegmentedGroups>
                  {scopeLabel && (
                    <DateRangeButton
                      type="button"
                      onClick={() => setScope({})}
                    >
                      {t('common:adminDashboard.clearScope')}
                    </DateRangeButton>
                  )}
                  <SegmentedControlCluster>
                    <Segmented
                      role="group"
                      aria-label={t('common:dashboard.metricTabLabel')}
                    >
                      {USAGE_METRICS.map((metric) => (
                        <SegmentedButton
                          key={metric}
                          type="button"
                          aria-pressed={usageMetric === metric}
                          $active={usageMetric === metric}
                          onClick={() => setUsageMetric(metric)}
                        >
                          {t(`common:dashboard.usageMetric.${metric}`)}
                        </SegmentedButton>
                      ))}
                    </Segmented>
                    <SegmentedDivider aria-hidden="true" />
                    {/* 사용자 지정 범위가 적용된 동안은 어느 프리셋도 눌린
                        상태가 아니다 — 차트가 그 프리셋을 보고 있지 않다. */}
                    <Segmented
                      role="group"
                      aria-label={t('common:adminDashboard.chartRangeLabel')}
                    >
                      {ADMIN_DASHBOARD_RANGE_DAYS.map((days) => {
                        const active = !appliedDateRange && chartDays === days;
                        return (
                          <SegmentedButton
                            key={days}
                            type="button"
                            aria-pressed={active}
                            $active={active}
                            onClick={() => selectChartDays(days)}
                          >
                            {rangeLabel(days)}
                          </SegmentedButton>
                        );
                      })}
                    </Segmented>
                  </SegmentedControlCluster>
                </SegmentedGroups>
              </CardHead>
              <DateRangeForm
                onSubmit={applyDateRange}
                aria-label={t('common:dashboard.dateRangeLabel')}
              >
                <DepartmentField>
                  {t('common:adminDashboard.filterDepartment')}
                  <SelectDropdown
                    size="sm"
                    value={departmentValue}
                    options={departmentOptions}
                    onChange={selectDepartment}
                    /* 전사 부서 수는 수백까지 갈 수 있어 스크롤만으로는 못
                     * 찾는다. 요청이 "이름으로 필터링" 이므로 검색이 본체다. */
                    searchable
                    searchPlaceholder={t(
                      'common:adminDashboard.filterDepartmentSearch',
                    )}
                    emptyMessage={t('common:adminDashboard.filterNoDepartments')}
                    aria-label={t('common:adminDashboard.filterDepartment')}
                  />
                </DepartmentField>
                <DateField>
                  {t('common:dashboard.dateStart')}
                  <DateInput
                    type="date"
                    value={dateStart}
                    aria-invalid={dateRangeError != null}
                    onChange={(event) => {
                      setDateStart(event.target.value);
                      setDateRangeError(null);
                    }}
                  />
                </DateField>
                <DateField>
                  {t('common:dashboard.dateEnd')}
                  <DateInput
                    type="date"
                    value={dateEnd}
                    aria-invalid={dateRangeError != null}
                    onChange={(event) => {
                      setDateEnd(event.target.value);
                      setDateRangeError(null);
                    }}
                  />
                </DateField>
                <DateRangeButton type="submit" $primary>
                  {t('common:dashboard.applyDateRange')}
                </DateRangeButton>
                {(dateStart || dateEnd || appliedDateRange) && (
                  <DateRangeButton type="button" onClick={clearDateRange}>
                    {t('common:dashboard.clearDateRange')}
                  </DateRangeButton>
                )}
                {dateRangeError && (
                  <DateRangeError role="alert">
                    {t(
                      `common:adminDashboard.dateRangeError.${dateRangeError}`,
                    )}
                  </DateRangeError>
                )}
              </DateRangeForm>
              {/* 실제 구간과 집계 단위를 말로 적는다. 프리셋은 기간만 말하고
                * 버킷 단위(일·주·월)는 기간에서 따라오므로, 이 줄이 없으면
                * "최근 90일" 의 막대 하나가 하루인지 한 주인지 알 길이 없다. */}
              {series.data && (
                <CardHintLine>
                  {t('common:adminDashboard.chartPeriod', {
                    start: series.data.start,
                    end: series.data.end,
                    bucket: t(
                      `common:dashboard.granularity.${series.data.granularity}`,
                    ),
                  })}
                </CardHintLine>
              )}
              {series.data ? (
                <ChartFrame $loading={series.isPlaceholderData} aria-busy={series.isPlaceholderData}>
                  <AdminUsageChart
                    data={series.data}
                    metric={usageMetric}
                    mode={chartMode}
                  />
                  {series.isPlaceholderData && (
                    <ChartLoading data-chart-loading role="status">
                      <RingSpinner size={14} />
                      {t('common:dashboard.loading')}
                    </ChartLoading>
                  )}
                </ChartFrame>
              ) : (
                <EmptyState>
                  {series.isLoading
                    ? t('common:dashboard.loading')
                    : t('common:adminDashboard.noUsageYet')}
                </EmptyState>
              )}
            </Card>

            {/* 3 — 본부 > 부서 */}
            <Card>
              <CardHead>
                <CardTitle>
                  <Building2 aria-hidden size={16} />
                  {t('common:adminDashboard.breakdownTitle')}
                </CardTitle>
                <Segmented
                  role="group"
                  aria-label={t('common:adminDashboard.breakdownRangeLabel')}
                >
                  {ADMIN_DASHBOARD_RANGE_DAYS.map((days) => (
                    <SegmentedButton
                      key={days}
                      type="button"
                      aria-pressed={breakdownDays === days}
                      $active={breakdownDays === days}
                      onClick={() => setBreakdownDays(days)}
                    >
                      {rangeLabel(days)}
                    </SegmentedButton>
                  ))}
                </Segmented>
              </CardHead>
              {/* 실제 대상 구간을 날짜로 보여준다 — "최근 365일" 만으로는
                * 기간을 넓혔는데 합계가 그대로일 때(그 이전 데이터가 아예
                * 없을 때) 값이 안 바뀐 것인지 같은 값인지 구분이 안 된다. */}
              <CardHintLine>
                {breakdownTable.data
                  ? `${t('common:adminDashboard.breakdownPeriod', {
                    start: breakdownTable.data.start,
                    end: breakdownTable.data.end,
                  })} · ${t('common:adminDashboard.breakdownHint')}`
                  : t('common:adminDashboard.breakdownHint')}
              </CardHintLine>
              {breakdownTable.data ? (
                <OrgBreakdown
                  data={breakdownTable.data}
                  mode={chartMode}
                  selected={scope}
                  onSelect={setScope}
                  labelFor={labelFor}
                />
              ) : (
                <EmptyState>
                  {breakdownTable.isLoading
                    ? t('common:dashboard.loading')
                    : t('common:adminDashboard.noUsageYet')}
                </EmptyState>
              )}
            </Card>
          </>
        )}
      </PageInner>
    </PageScroll>
  );
};
